import React from "react";
import * as api from "./sequences-api";

/*
 * Shared pieces of the sequence editor (2026-10-08 redesign, PlunkDocs mockups/video-editors-redesign-2026-10-08.html
 * screens 3 to 5): scene lengths, thumbnails, labels and the small controls every panel uses. Classes are `sx-`.
 */

export const SIZES = [["16:9", "Wide", "16:9"], ["9:16", "Story", "9:16"], ["1:1", "Square", "1:1"]];
export const sizeName = f => {
  const r = SIZES.find(([k]) => k === f);
  return r ? `${r[1]} ${r[2]}` : f;
};
const has = v => v !== null && v !== undefined && v !== "" && !isNaN(Number(v));
/** The length the server would use (estimate first), the same rule the storyboard has always used. */
export const segSeconds = s => has(s.estimatedSeconds) ? Number(s.estimatedSeconds) : has(s.durationSeconds) ? Number(s.durationSeconds) : has(s.narrationSeconds) ? Math.max(3, Number(s.narrationSeconds) + 0.6) : s.kind === "card" ? 3 : s.narration ? Math.max(3, api.wordCount(s.narration) / 2.6 + 0.6) : 4;
/** The length shown on the timeline: a local override wins at once (the server estimate catches up on save). */
export const lenOf = s => has(s.durationSeconds) ? Number(s.durationSeconds) : segSeconds(s);
export const voiceSeconds = s => has(s.narrationSeconds) ? Number(s.narrationSeconds) : s.narration ? api.wordCount(s.narration) / 2.6 : 0;
export const isOverride = s => has(s.durationSeconds);
export const isMedia = s => !!s && (s.kind === "clip" || s.kind === "recording");
export const isCard = s => !!s && s.kind === "card";
export const cardStyle = (s, i) => s && s.card && s.card.style || (i === 0 ? "title" : "end");
export const thumbOf = s => !s ? null : s.kind === "still" || s.kind === "card" ? s.mediaUrl || s.thumbnailUrl : s.thumbnailUrl || null;
export const kindLabel = (s, i) => !s ? "" : s.kind === "card" ? cardStyle(s, i) === "end" ? "End card" : "Title card" : s.kind === "clip" ? "Video clip" : s.kind === "recording" ? "Screen recording" : "Photo";
export const snap = v => Math.round(Number(v) * 10) / 10;
export const fmtLen = s => `${(Math.round(Number(s || 0) * 10) / 10).toFixed(1)} s`;
export const captionLine = t => {
  const w = String(t || "").trim().split(/\s+/).filter(Boolean);
  return w.length ? w.slice(0, 7).join(" ") + (w.length > 7 ? "…" : "") : "";
};
export const sixWords = t => {
  const w = String(t || "").split(/\s+/);
  return w.length > 6 && w.slice(6).some(Boolean) ? w.slice(0, 6).join(" ") : t;
};

/** Segmented control in the image editor's style (one choice). */
export const SxSeg = ({
  list,
  value,
  onChange,
  disabled,
  label,
  wrap
}) => /*#__PURE__*/React.createElement("span", {
  className: "sx-seg" + (wrap ? " wrap" : ""),
  role: "group",
  "aria-label": label
}, list.map(([k, l, title]) => /*#__PURE__*/React.createElement("button", {
  type: "button",
  key: String(k),
  className: value === k ? "on" : "",
  "aria-pressed": value === k,
  disabled: disabled,
  title: title || undefined,
  onClick: () => onChange(k)
}, l)));
export const SxField = ({
  label,
  hint,
  children,
  cls
}) => /*#__PURE__*/React.createElement("label", {
  className: "sx-field" + (cls ? " " + cls : "")
}, /*#__PURE__*/React.createElement("span", {
  className: "sx-label"
}, label, hint ? /*#__PURE__*/React.createElement("i", null, hint) : null), children);
export const SxGroup = ({
  label,
  hint,
  children,
  cls
}) => /*#__PURE__*/React.createElement("div", {
  className: "sx-field" + (cls ? " " + cls : "")
}, /*#__PURE__*/React.createElement("span", {
  className: "sx-label"
}, label, hint ? /*#__PURE__*/React.createElement("i", null, hint) : null), children);
export const SxCheck = ({
  checked,
  onChange,
  disabled,
  children
}) => /*#__PURE__*/React.createElement("label", {
  className: "sx-check"
}, /*#__PURE__*/React.createElement("input", {
  type: "checkbox",
  checked: !!checked,
  disabled: disabled,
  onChange: e => onChange(e.target.checked)
}), /*#__PURE__*/React.createElement("span", null, children));

/** A small picture of one scene: the photo / clip poster, or the card drawn as a card. */
export function SceneThumb({
  seg,
  i,
  brand
}) {
  if (!seg) return null;
  const accent = brand && brand.accent || "#1d4ed8";
  if (isCard(seg)) {
    const end = cardStyle(seg, i) === "end";
    return /*#__PURE__*/React.createElement("span", {
      className: "sx-cardthumb" + (end ? " end" : ""),
      style: end ? undefined : {
        background: `linear-gradient(135deg, ${accent}, #1c1f24)`
      }
    }, /*#__PURE__*/React.createElement("b", null, seg.card && seg.card.headline || (end ? "End card" : "Title card")));
  }
  const t = thumbOf(seg);
  if (t) return /*#__PURE__*/React.createElement("img", {
    src: t,
    alt: "",
    draggable: false,
    loading: "lazy",
    className: seg.kind === "recording" || seg.fit === "contain" ? "contain" : ""
  });
  if (seg.mediaUrl && isMedia(seg)) return /*#__PURE__*/React.createElement("video", {
    src: seg.mediaUrl,
    muted: true,
    playsInline: true,
    preload: "metadata",
    className: seg.kind === "recording" ? "contain" : ""
  });
  return /*#__PURE__*/React.createElement("span", {
    className: "sx-nomedia"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons"
  }, isMedia(seg) ? "movie" : "image"));
}